// Item 409 — pure geometry for the pose overlay on the event video.
import type { PoseFrame } from '@/types'

export interface Rect { x: number; y: number; w: number; h: number }
export interface Pt { x: number; y: number }

/** Where an `object-contain` video really paints inside its element box (the letterbox). */
export function videoContentRect(videoW: number, videoH: number, boxW: number, boxH: number): Rect {
  if (!(videoW > 0) || !(videoH > 0) || !(boxW > 0) || !(boxH > 0)) return { x: 0, y: 0, w: 0, h: 0 }
  const scale = Math.min(boxW / videoW, boxH / videoH)
  const w = videoW * scale
  const h = videoH * scale
  return { x: (boxW - w) / 2, y: (boxH - h) / 2, w, h }
}

/** A point in source pixels (frameW x frameH) onto the painted video rect. */
export function toScreen(pt: [number, number, ...number[]] | Pt, frameW: number, frameH: number, rect: Rect): Pt {
  const px = Array.isArray(pt) ? pt[0] : pt.x
  const py = Array.isArray(pt) ? pt[1] : pt.y
  if (!(frameW > 0) || !(frameH > 0)) return { x: rect.x, y: rect.y }
  return { x: rect.x + (px / frameW) * rect.w, y: rect.y + (py / frameH) * rect.h }
}

/**
 * The stored frame nearest to playback time `timeS`, if it lies within one stride of it.
 * Frame indexes are SOURCE frames (the played clip has every frame at the source fps), so time -> t * fps.
 * `stride` defaults to the smallest gap between stored frames (1 when there is a single frame).
 */
export function frameAt(timeS: number, fps: number, frames: PoseFrame[], stride?: number): PoseFrame | null {
  if (!(fps > 0) || !Number.isFinite(timeS) || frames.length === 0) return null
  let s = stride
  if (!(s && s > 0)) {
    s = Infinity
    for (let i = 1; i < frames.length; i++) {
      const gap = frames[i].f - frames[i - 1].f
      if (gap > 0 && gap < s) s = gap
    }
    if (!Number.isFinite(s)) s = 1
  }
  const target = timeS * fps
  let best: PoseFrame | null = null
  let bestD = Infinity
  for (const fr of frames) {
    const d = Math.abs(fr.f - target)
    if (d < bestD) { best = fr; bestD = d }
  }
  return best && bestD <= s ? best : null
}

/** The 16 limb pairs drawn between the 17 COCO keypoints: legs, hips, trunk, shoulders, arms, face. */
export const COCO_EDGES: ReadonlyArray<readonly [number, number]> = [
  [15, 13], [13, 11], [16, 14], [14, 12], // legs
  [11, 12], [5, 11], [6, 12], [5, 6],     // hips, trunk, shoulders
  [5, 7], [7, 9], [6, 8], [8, 10],        // arms
  [0, 1], [0, 2], [1, 3], [2, 4],         // nose, eyes, ears
]

/** Left and right wrist. */
export const WRISTS: readonly number[] = [9, 10]
